import type { LabelPreset } from "../labels";
import { inchesToPoints, type SheetTemplate } from "./sheetTemplates";

export type RectPt = { x: number; y: number; w: number; h: number };

export function shouldRotateOnSheet(preset: LabelPreset, sheet: SheetTemplate): boolean {
  return Boolean(preset.rotateCCW90OnSheet || sheet.rotateCCW90OnSheet);
}

// Compute one-page grid cell rectangles (PDF points, origin at bottom-left)
export function computePageCellRects(sheet: SheetTemplate): RectPt[] {
  const pageW = inchesToPoints(sheet.pageSizeIn.w);
  const pageH = inchesToPoints(sheet.pageSizeIn.h);
  const left = inchesToPoints(sheet.leftMarginIn);
  const top = inchesToPoints(sheet.topMarginIn);
  const cellW = inchesToPoints(sheet.labelSizeIn.w);
  const cellH = inchesToPoints(sheet.labelSizeIn.h);
  const gapH = inchesToPoints(sheet.hSpacingIn);
  const gapV = inchesToPoints(sheet.vSpacingIn);

  const rects: RectPt[] = [];
  for (let r = 0; r < sheet.rows; r++) {
    for (let c = 0; c < sheet.columns; c++) {
      const x = left + c * (cellW + gapH);
      const yTop = pageH - top - r * (cellH + gapV);
      const y = yTop - cellH;
      rects.push({ x, y, w: cellW, h: cellH });
    }
  }
  return rects;
}

// Within a cell, compute a centered target box scaled to fit with padding and a given aspect ratio (w/h)
export function fitBoxCentered(cell: RectPt, paddingPct: number, aspectRatio: number): RectPt {
  const pad = Math.max(0, Math.min(0.2, paddingPct));
  const innerW = cell.w * (1 - pad);
  const innerH = cell.h * (1 - pad);
  const cellAspect = innerW / innerH;
  let w = innerW;
  let h = innerH;
  if (cellAspect > aspectRatio) {
    // Cell is wider than content → limit by height
    h = innerH;
    w = h * aspectRatio;
  } else {
    // Cell is taller/narrower → limit by width
    w = innerW;
    h = w / aspectRatio;
  }
  const x = cell.x + (cell.w - w) / 2;
  const y = cell.y + (cell.h - h) / 2;
  return { x, y, w, h };
}



// Compose an SVG sheet by tiling the provided inner artwork into each cell.
export function composeSheetSvg(
  artworkInner: string,
  artworkSizePt: { w: number; h: number },
  preset: LabelPreset,
  sheet: SheetTemplate,
  opts?: { padIn?: number }
): string {
  const rotate = shouldRotateOnSheet(preset, sheet);
  const pageW = inchesToPoints(sheet.pageSizeIn.w);
  const pageH = inchesToPoints(sheet.pageSizeIn.h);
  const cells = computePageCellRects(sheet);
  const padIn = Math.max(0, opts?.padIn ?? 0.04);
  const padPt = inchesToPoints(padIn);

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${pageW}px" height="${pageH}px" viewBox="0 0 ${pageW} ${pageH}" preserveAspectRatio="none">`
  );

  const debug = (import.meta as any).env?.SHEET_DEBUG === "1";

  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    const cx = cell.x + cell.w / 2;
    const cy = cell.y + cell.h / 2;
    const innerBox = { x: cell.x + padPt, y: cell.y + padPt, w: cell.w - 2 * padPt, h: cell.h - 2 * padPt };
    const scale = Math.min(innerBox.w / artworkSizePt.w, innerBox.h / artworkSizePt.h);
    const aw = artworkSizePt.w * scale;
    const ah = artworkSizePt.h * scale;
    const ax = cx - aw / 2;
    const ay = cy - ah / 2;

    if (debug) {
      parts.push(`<rect x="${cell.x}" y="${cell.y}" width="${cell.w}" height="${cell.h}" fill="none" stroke="#bbb" stroke-width="0.5"/>`);
    }

    const wrapperOpen = rotate
      ? `<g data-cell transform="translate(${cx}, ${cy}) rotate(-90) translate(${-cx}, ${-cy})">`
      : `<g data-cell>`;
    parts.push(wrapperOpen);
    parts.push(`<g transform="translate(${ax}, ${ay}) scale(${scale})">${artworkInner}</g>`);
    parts.push(`</g>`);
  }

  parts.push(`</svg>`);
  return parts.join("\n");
}

